import { useEffect, useRef } from 'react';
import { useStore } from '@xyflow/react';
import type { HelperLine } from "@/features/canvas/engine/primitives/geometry/helper-lines";
import styles from './helper-lines-renderer.module.css';

interface HelperLinesRendererProps {
  horizontal?: HelperLine;
  vertical?: HelperLine;
}

/**
 * Renders alignment guide lines on a canvas overlay during node drag/resize.
 * Solid line for edge alignment, dashed for center alignment.
 */
export function HelperLinesRenderer({ horizontal, vertical }: HelperLinesRendererProps) {
  const { width, height, transform } = useStore(s => ({
    width: s.width,
    height: s.height,
    transform: s.transform,
  }));

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    const dpi = window.devicePixelRatio;
    canvas.width = width * dpi;
    canvas.height = height * dpi;
    ctx.scale(dpi, dpi);
    ctx.clearRect(0, 0, width, height);

    // Use --primary CSS var resolved from the canvas element
    const primary = getComputedStyle(canvas).getPropertyValue('--primary').trim() || '#5E6AD2';
    ctx.strokeStyle = primary;
    ctx.lineWidth = 1;

    if (vertical) {
      ctx.beginPath();
      ctx.setLineDash(vertical.anchorName === 'centerX' ? [4, 4] : []);
      const x = vertical.position * transform[2] + transform[0];
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    if (horizontal) {
      ctx.beginPath();
      ctx.setLineDash(horizontal.anchorName === 'centerY' ? [4, 4] : []);
      const y = horizontal.position * transform[2] + transform[1];
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height, transform, horizontal, vertical]);

  if (!horizontal && !vertical) return null;

  return <canvas ref={canvasRef} className={styles.canvas} />;
}
